import { Routes } from '@angular/router';

/**
 * The signed-in shell's route subtree.
 *
 * The Shell hosts ion-tabs; each of the four tabs is a thin view wrapper
 * (in tabs.ts) that owns its `ion-content` and embeds the page component
 * directly. The wrappers live in ion-tabs' own outlet, so Ionic's tab
 * machinery keeps exactly one tab in the foreground and retains each tab's
 * scroll.
 *
 * The tab ROOTS on the bar (Dashboard, Database) plus the two detail routes
 * (clubs/:id, accounts/:id) and the off-bar pages (Audit log, Settings and
 * My account, reached from the tab bar's More sheet). The clubs and accounts
 * dashboards live inside the Dashboard as its "Clubs" and "Accounts"
 * sub-tabs, deep-linkable via /dashboard/:section (see the config comment
 * below).
 * The detail routes are FLAT - direct
 * children of ion-tabs' outlet, at the SAME level as the tab roots, NOT nested
 * under them. That is deliberate: ion-tabs derives a view's stackId from the
 * FIRST URL segment. Neither clubs/:id nor accounts/:id has a matching list
 * route any more, so each stands alone in its own stack ('clubs' / 'accounts')
 * - arriving from the dashboard is a tab-switch, and each detail's own back
 * control / history returns there. A nested ion-router-outlet breaks
 * foregrounding and throws NG0950, so each detail is its own
 * ion-content-owning wrapper (in tabs.ts) that reads the ':id' param via
 * component input binding.
 *
 * The Shell component itself is supplied by app.routes.ts as the parent
 * `component`; this array is its `children`.
 *
 * Each route declares its bottom chrome via `data.tabBar`: the four tab ROOTS
 * carry `tabBar: true` (show the shell's shared tab bar); the DETAIL routes
 * carry `tabBar: false` (the shared bar slides out - the page shows its own
 * action bar, or none). The Shell reads the deepest active route's `data` to
 * decide, so visibility is declarative per route (Ionic's children-vs-siblings
 * idea, expressed in route config) without giving up the flat-child routing
 * that makes push/pop and swipe-back work.
 */
export const shellChildren: Routes = [
  // The dashboard's sub-tabs are deep-linkable: /dashboard/:section maps to
  // the panes (overview | clubs | accounts | usage). ONE component-bearing
  // config: param-only navigations REUSE the DashboardTab instance (no Ionic
  // page push, no remount), so the kit strip's veil motion and per-tab
  // scroll ledger carry across section changes. Plain /dashboard redirects
  // to the first section; Ionic's tab memory restores the last-visited
  // section URL when returning via the bottom bar. stackId still derives
  // from the FIRST segment, so every section stays in the 'dashboard' stack.
  { path: 'dashboard', redirectTo: 'dashboard/overview', pathMatch: 'full' },
  {
    path: 'dashboard/:section',
    data: { tabBar: true },
    loadComponent: () => import('./tabs').then((m) => m.DashboardTab),
  },
  // The Database page's sections follow the same deep-link recipe:
  // /database/:section (clubs | accounts | activity).
  { path: 'database', redirectTo: 'database/clubs', pathMatch: 'full' },
  {
    // The full add-club walkthrough (basics -> branding -> white label ->
    // review). Off the bar; three segments, so it never collides with the
    // ':section' route below.
    path: 'database/clubs/add',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.AddClubTab),
  },
  {
    // The same wizard as 'add', handed a club to load. FOUR segments, so it
    // cannot collide with 'database/clubs/add' or the ':section' route.
    path: 'database/clubs/:clubId/edit',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.EditClubTab),
  },
  {
    path: 'database/:section',
    data: { tabBar: true },
    loadComponent: () => import('./tabs').then((m) => m.DatabaseTab),
  },
  // The clubs portfolio is the Dashboard's "Clubs" sub-tab (no route of its
  // own). Club detail stays a flat route; arriving from the dashboard is a
  // tab-switch, and its back / swipe returns there. Its sections follow the
  // deep-link recipe: /clubs/:id/:section (overview | transactions |
  // members), one component-bearing config so section changes reuse the
  // page.
  { path: 'clubs/:id', redirectTo: 'clubs/:id/overview', pathMatch: 'full' },
  // The club's activity drill-in (the actions menu's 'View activity'). A
  // LITERAL path, declared BEFORE the :section config so the router matches
  // it first; same first segment, so it shares the 'clubs' stack and PUSHES
  // onto the club detail (back pops home). Transactions is a SECTION of the
  // detail page, not a drill-in.
  {
    path: 'clubs/:id/activity',
    data: { tabBar: false, variant: 'activity' },
    loadComponent: () => import('./tabs').then((m) => m.ClubTrailTab),
  },
  {
    path: 'clubs/:id/:section',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.ClubDetailTab),
  },
  // The accounts dashboard is the Dashboard's "Accounts" sub-tab (no route of
  // its own). Account detail stays a flat route; back returns to the dashboard.
  // Account detail follows the club detail recipe exactly: sections are
  // deep-linkable (/accounts/:id/:section - overview | details), one
  // component-bearing config so section changes reuse the page.
  { path: 'accounts/:id', redirectTo: 'accounts/:id/overview', pathMatch: 'full' },
  {
    path: 'accounts/:id/:section',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.AccountDetailTab),
  },
  // Not on the bar: the More-sheet pages (Audit log, Settings, My account).
  // tabBar: false because no bar item matches them - the bar slides out and
  // the page's back control brings you home.
  {
    path: 'audit',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.AuditTab),
  },
  {
    path: 'settings',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.SettingsTab),
  },
  {
    // The operator's walkthrough: record -> season -> white label -> builds.
    path: 'creating-a-club',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.CreatingAClubTab),
  },
  {
    // The build half of the operator's docs (the guide's step 7 links here).
    path: 'building-club-apps',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.BuildingClubAppsTab),
  },
  {
    path: 'my-account',
    data: { tabBar: false },
    loadComponent: () => import('./tabs').then((m) => m.MyAccountTab),
  },
  // Land the blank root straight on the first section. This is a SINGLE-hop
  // redirect on purpose: Ionic's ion-router-outlet does not reliably chain a
  // two-stage redirect ('' -> dashboard -> dashboard/overview) through the tab
  // stacks - the Shell mounts but the outlet stays empty. Going direct to
  // dashboard/overview populates the 'dashboard' stack in one navigation. The
  // 'dashboard' -> 'dashboard/overview' redirect above still covers direct
  // hits to /dashboard.
  { path: '', redirectTo: 'dashboard/overview', pathMatch: 'full' },
];
